import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useOrgFormatting } from '../branding/BrandProvider';

interface EventCardProps {
  event: {
    id: number;
    slug?: string;
    title: string;
    startAt: string | Date;
    flyerUrl?: string;
    minPrice?: number;
  };
  className?: string;
}

export function EventCard({ event, className = '' }: EventCardProps) {
  const { formatPrice, formatDate } = useOrgFormatting();

  const eventUrl = `/events/${event.slug || event.id}`;
  const eventDate = typeof event.startAt === 'string' ? new Date(event.startAt) : event.startAt;
  
  const formattedDate = formatDate(eventDate, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });

  const formattedTime = formatDate(eventDate, {
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <Link href={eventUrl} className={`group block ${className}`}>
      <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-200">
        {/* Event Image */}
        <div className="relative aspect-[4/3] bg-gray-100">
          {event.flyerUrl ? (
            <Image
              src={event.flyerUrl}
              alt={`${event.title} flyer`}
              width={400}
              height={300}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
              sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
            />
          ) : (
            <div 
              className="w-full h-full flex items-center justify-center text-white"
              style={{ backgroundColor: 'var(--color-primary)' }}
            >
              <div className="text-center">
                <div className="text-2xl font-bold mb-1">
                  {eventDate.getDate()}
                </div>
                <div className="text-sm opacity-90">
                  {eventDate.toLocaleDateString('en-GB', { month: 'short' })}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Event Details */}
        <div className="p-4">
          <h3 className="font-semibold text-lg text-gray-900 mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors">
            {event.title}
          </h3>
          
          <div className="flex items-center justify-between text-sm text-gray-600">
            <div>
              <div className="font-medium">{formattedDate}</div>
              <div>{formattedTime}</div>
            </div>
            
            {event.minPrice && (
              <div className="text-right">
                <div className="text-xs text-gray-500">From</div>
                <div className="font-semibold text-gray-900">
                  {formatPrice(event.minPrice)}
                </div>
              </div>
            )}
          </div>

          {/* CTA Button */}
          <div className="mt-4">
            <div 
              className="w-full py-2 px-4 rounded-md text-center text-white font-medium text-sm hover:opacity-90 transition-opacity"
              style={{ backgroundColor: 'var(--color-primary)' }}
            >
              View Event
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}
